import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../../../i18n/lazyNamespaces";
import { MAX_PLATFORM_PROBE_SOCKETS } from "../../../hooks/useWorkspaceProbeSockets";
import { presenceCardSurface } from "../../../utils/teamPresence";
import { getTeamPresenceScreenTitle } from "../../../utils/teamPresenceScreenTitle";
import { BusyButton } from "./shared";
import PlatformPresenceUserCard from "./PlatformPresenceUserCard";

const PlatformOnlineTabPanel = ({ presence, hideToolbarRefresh = false }) => {
  const { t } = useTranslation("app");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();

  const [livePresenceTickAt, setLivePresenceTickAt] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setLivePresenceTickAt(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);

  const screenTitleForPath = useCallback((path) => getTeamPresenceScreenTitle(path, t), [t]);

  const {
    loadingDominios,
    dominiosError,
    workspaceHostList,
    truncatedTotal,
    mainSocketConnected,
    socketStats,
    hostsProbed,
    probesAllConnected,
    probeWorkspaceHosts,
    onlineItems,
    onlinePresenceStats,
    refreshList,
  } = presence;

  const onlineCardSurface = useMemo(() => presenceCardSurface(theme, "online"), [theme]);

  useEffect(() => {
    ensureNamespaceLoaded("settings");
  }, []);

  if (loadingDominios && !workspaceHostList.length && !dominiosError) {
    return (
      <Box sx={{ py: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (dominiosError) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error">{dominiosError}</Typography>
        <BusyButton sx={{ mt: 2 }} variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={refreshList}>
          {t("billingPage.refresh")}
        </BusyButton>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
          {t("platformPage.onlineIntro")}
        </Typography>
        {!hideToolbarRefresh ? (
          <BusyButton
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refreshList}
            loading={loadingDominios}
            loadingText={t("billingPage.refresh")}
          >
            {t("billingPage.refresh")}
          </BusyButton>
        ) : null}
      </Stack>

      {!mainSocketConnected ? (
        <Alert severity="warning">{t("platformPage.onlineMainSocketDisconnected")}</Alert>
      ) : null}

      {truncatedTotal ? (
        <Alert severity="info">{t("platformPage.onlineWorkspaceCap", { max: MAX_PLATFORM_PROBE_SOCKETS })}</Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block">
        {t("platformPage.onlineMetaSocket", {
          count: onlinePresenceStats?.active ?? onlineItems.filter((r) => !r.presenceStale).length,
          workspaces: workspaceHostList.length,
          probes: hostsProbed.length,
          connected: socketStats.connected,
          total: socketStats.total,
        })}
        {(onlinePresenceStats?.stale ?? 0) > 0
          ? ` ${t("platformPage.onlineMetaStaleSuffix", { count: onlinePresenceStats.stale })}`
          : null}
      </Typography>

      {probeWorkspaceHosts.length > 0 && !probesAllConnected ? (
        <Typography variant="caption" color="text.secondary">
          {t("platformPage.onlineSocketsConnecting")}
        </Typography>
      ) : null}

      {!onlineItems.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">{t("platformPage.onlineEmpty")}</Typography>
        </Paper>
      ) : (
        <>
          {!(onlinePresenceStats?.active ?? onlineItems.some((r) => !r.presenceStale)) &&
          (onlinePresenceStats?.stale ?? 0) > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {t("platformPage.onlineOnlyStaleHint")}
            </Typography>
          ) : null}
          <Grid container spacing={1.5}>
            {onlineItems.map((row) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={row.key}>
                <PlatformPresenceUserCard
                  row={row}
                  onlineCardSurface={onlineCardSurface}
                  tLive={tLive}
                  screenTitleForPath={screenTitleForPath}
                  livePresenceNowMs={livePresenceTickAt}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Stack>
  );
};

export default PlatformOnlineTabPanel;
